import { type PaletteOptions } from "@mui/material/styles";

const COLOR_NAVBAR_AVATAR_BACKGROUND = "#FFFFFF1A"; // 10% opacity
const COLOR_NAVBAR_AVATAR_TEXT = "#FFFFFFE6"; // 90% opacity
const COLOR_NAVBAR_GRADIENT_START = "#001529";
const COLOR_NAVBAR_GRADIENT_END = "#00101F";
const COLOR_NAVBAR_GRADIENT = `linear-gradient(90.15deg, ${COLOR_NAVBAR_GRADIENT_START} 0%, ${COLOR_NAVBAR_GRADIENT_END} 78.74%)`;
const COLOR_NAVBAR_ITEM_CONTENT = "#F7F7F799"; // 60% opacity
const COLOR_NAVBAR_ITEM_CONTENT_SELECTED = "#FFFFFF";
const COLOR_NAVBAR_ITEM_SELECTED_BACKGROUND = "#FFFFFF0D"; // 5% opacity
const COLOR_NAVBAR_SECTION_TITLE = "#F7F7F766"; // 40% opacity
const COLOR_NAVBAR_SECTION_DIVIDER = "#F7F7F71A"; // 10% opacity
const COLOR_NAVBAR_USERNAME = "#FFFFFF";

const COLOR_FLOATING_NAVIGATION_BACKGROUND =
  "linear-gradient(123.53deg, rgba(22, 25, 50, 0.64) 27.51%, rgba(62, 57, 47, 0.72) 71.21%)";

const COLOR_FLOATING_NAVIGATION_SELECTED_BACKGROUND = "#9A9A9A69"; // 40% opacity

// Shift card gradient border colors
const COLOR_SHIFT_CARD_GRADIENT_START = "#FF7E7E"; // Pink
const COLOR_SHIFT_CARD_GRADIENT_MIDDLE = "#C546AF"; // Purple
const COLOR_SHIFT_CARD_GRADIENT_END = "#D8D8D8"; // Gray
const COLOR_SHIFT_CARD_BORDER_GRADIENT = `linear-gradient(135deg, ${COLOR_SHIFT_CARD_GRADIENT_START} 0%, ${COLOR_SHIFT_CARD_GRADIENT_MIDDLE} 15%, ${COLOR_SHIFT_CARD_GRADIENT_END} 100%)`;

// Avatar initials fallback background gradient
const COLOR_AVATAR_INITIALS_GRADIENT_START = "#9F9F9F";
const COLOR_AVATAR_INITIALS_GRADIENT_END = "#393939";
const COLOR_AVATAR_INITIALS_BACKGROUND = `linear-gradient(to bottom right, ${COLOR_AVATAR_INITIALS_GRADIENT_START}, ${COLOR_AVATAR_INITIALS_GRADIENT_END})`;

// Berlin color tokens
const COLOR_STRONG = "#17181E";
const COLOR_BASE = "#282932";
const COLOR_PRIMARY = "#1890FF";
const COLOR_PRIMARY_DARK = "#663DBF";
const COLOR_SECONDARY = "#201E4B";
const COLOR_MUTED = "#00000099"; // 60% opacity
const COLOR_DANGER = "#DD1717";
const COLOR_INACTIVE = "#00000059"; // 35% opacity
export const COLOR_INACTIVE_SOLID = "#A6A6A6"; // solid equivalent of COLOR_INACTIVE on white background, useful when opacity is not desired (e.g., strikethrough)

// Surface and interaction tokens
const COLOR_SURFACE = "#FFFFFF";
const COLOR_TEXT_PRIMARY = "#000000DE"; // MUI default text.primary (rgba(0, 0, 0, 0.87))
const COLOR_SUBTLE_ACTION_HOVER_BACKGROUND = "rgba(0, 0, 0, 0.04)";
const COLOR_LIST_ITEM_SELECTED_BACKGROUND = "rgba(0, 0, 0, 0.08)";

// Berlin inverted color tokens
const COLOR_INVERTED_STRONG = "#FFFFFFE6"; // 90% opacity
const COLOR_INVERTED_BASE = "#FFFFFFE6"; // 90% opacity
const COLOR_INVERTED_MUTED = "#FFFFFFB3"; // 70% opacity
const COLOR_INVERTED_INACTIVE = "#FFFFFF59"; // 35% opacity

// Berlin background-color tokens
const COLOR_BACKGROUND_COLOR_BASE = "#F7F7F7";
const COLOR_BACKGROUND_COLOR_BASE_DARK = "#FBF7F6";
const COLOR_BACKGROUND_COLOR_MEDIUM_GREY = "#EBEBEB";
const COLOR_BACKGROUND_COLOR_MUTED = "#0000000D"; // 5% opacity
const COLOR_BACKGROUND_COLOR_INACTIVE = "#00000026"; // 15% opacity
const COLOR_BACKGROUND_COLOR_ALPHA = "#FBF7F6BF"; // 75% opacity
const COLOR_BACKGROUND_COLOR_STANDOUT = "#FFFFFF";
const COLOR_BACKGROUND_COLOR_PRIMARY = "#1890FF14"; // 8% opacity
const COLOR_BACKGROUND_COLOR_AM = "#FFD7A6";
const COLOR_BACKGROUND_COLOR_AM_DARK = "#FFFFFF";
const COLOR_BACKGROUND_COLOR_PM = "#A5E3FB";
const COLOR_BACKGROUND_COLOR_PM_DARK = "#FFFFFF";
const COLOR_BACKGROUND_COLOR_NOC = "#E9C3F8";
const COLOR_BACKGROUND_COLOR_NOC_DARK = "#FFFFFF";
const COLOR_BACKGROUND_COLOR_WARNING = "#CE72100F"; // 6% opacity
const COLOR_BACKGROUND_COLOR_ERROR = "#DD17170F"; // 6% opacity
const COLOR_BACKGROUND_COLOR_PRIMARY_SOLID = "#EDF7FF"; // Light blue solid for invite sent state
// Berlin chat message background colors
const COLOR_CHAT_MESSAGE_INCOMING = "#0000000D"; // 5% opacity - light gray for incoming messages
const COLOR_CHAT_MESSAGE_OUTGOING = COLOR_PRIMARY; // Primary blue for outgoing messages

// Berlin background-color inverted tokens
const COLOR_BACKGROUND_COLOR_INVERTED_MUTED = "#FFFFFF0D"; // 5% opacity

// Berlin border-color tokens
const COLOR_BORDER_COLOR_BASE = "#0000001A"; // 10% opacity
const COLOR_BORDER_COLOR_MUTED = "#0000000D"; // 5% opacity
const COLOR_BORDER_COLOR_STRONG = "#00000026"; // 15% opacity
const COLOR_BORDER_COLOR_PRIMARY = "#1890FF33"; // 20% opacity
const COLOR_BORDER_COLOR_WARNING = "#CE721033"; // 20% opacity
const COLOR_BORDER_COLOR_ERROR = "#DD171733"; // 20% opacity

// Berlin links tokens
const COLOR_LINKS = "#146BF8";

// Berlin button style tokens
const COLOR_BUTTON_TRANSPARENT = "#FFFFFF00"; // 0% opacity
const COLOR_BUTTON_PRIMARY_HOVER = "#329DFF";
const COLOR_BUTTON_SECONDARY = "#26222B";
const COLOR_BUTTON_SECONDARY_HOVER = "#46404D";
const COLOR_BUTTON_DISABLED_COLOR = "#878787";
const COLOR_BUTTON_DISABLED_BACKGROUND = "#D7D7D7";
const COLOR_BUTTON_LINK_HOVER = "#065BE5";
const COLOR_BUTTON_LINK_HOVER_BACKGROUND = "#146BF812"; // 7% opacity
const COLOR_BUTTON_LINK_DISABLED = "#8B8B8B";
const COLOR_BUTTON_LINK_INVERTED = "#FFFFFFCC"; // 80% opacity
const COLOR_BUTTON_LINK_INVERTED_HOVER_BACKGROUND = "#FFFFFF1A"; // 10% opacity
const COLOR_BUTTON_LINK_INVERTED_HOVER_BORDER = "#FFFFFF1A"; // 10% opacity
const COLOR_BUTTON_LINK_INVERTED_DISABLED = "#FFFFFF33"; // 20% opacity
const COLOR_BUTTON_MUTED_COLOR = "#17181E";
const COLOR_BUTTON_MUTED_HOVER_COLOR = "#393B48";
const COLOR_BUTTON_MUTED_HOVER_BACKGROUND = "#FFFFFF";
const COLOR_BUTTON_MUTED_HOVER_BORDER = "#EBEBEB";
const COLOR_BUTTON_OUTLINE_BORDER = "#DDDDDD";
const COLOR_BUTTON_OUTLINE_DISABLED = "#AEAEAE";
const COLOR_BUTTON_OUTLINE_DISABLED_BORDER = "#CBCBCB";
const COLOR_BUTTON_OUTLINE_INVERTED = "#FFFFFFCC"; // 80% opacity
const COLOR_BUTTON_OUTLINE_INVERTED_BORDER = "#FFFFFF1A"; // 10% opacity
const COLOR_BUTTON_OUTLINE_INVERTED_HOVER_BACKGROUND = "#FFFFFF1A"; // 10% opacity
const COLOR_BUTTON_OUTLINE_INVERTED_HOVER_BORDER = "#FFFFFF33"; // 20% opacity
const COLOR_BUTTON_OUTLINE_INVERTED_DISABLED = "#FFFFFF33"; // 20% opacity
const COLOR_BUTTON_OUTLINE_INVERTED_DISABLED_BACKGROUND = "#FFFFFF0D"; // 5% opacity
const COLOR_BUTTON_OUTLINE_INVERTED_DISABLED_BORDER = "#FFFFFF0D"; // 5% opacity
const COLOR_BUTTON_WARNING = "#CE7210";
const COLOR_BUTTON_WARNING_HOVER = "#D8862E";

// Berlin shifts/progresses tokens
const COLOR_PROGRESS_POSITIVE = "#2FB690";
const COLOR_PROGRESS_PENDING = "rgba(47, 182, 144, 0.3)"; // 30% opacity of positive
const COLOR_PROGRESS_NEGATIVE = "#F06868";
const COLOR_PROGRESS_NEUTRAL = "#D9D9D9";

// Berlin status chip tokens
const COLOR_STATUS_WARNING = "#CE7210";
const COLOR_STATUS_PENDING = "#EF6C00"; // Ember orange
const COLOR_BACKGROUND_COLOR_SUCCESS = "#2FB6900F"; // 6% opacity of positive
const COLOR_BACKGROUND_COLOR_PENDING = "#EF6C000F"; // 6% opacity of pending orange

// Status ring colors for shift tile worker avatars (from Figma node 11:2563)
const COLOR_STATUS_RING_POSITIVE = "#45d315";
const COLOR_STATUS_RING_WARNING = "#fab958";
const COLOR_STATUS_RING_NEGATIVE = "#ff5a5a";
const COLOR_STATUS_RING_NEUTRAL = "#4a9bff";

export interface ButtonStateColorMap {
  color: string;
  backgroundColor: string;
  borderColor: string;
}

export const muiThemePalette: PaletteOptions = {
  primary: {
    main: COLOR_PRIMARY,
  },
  link: {
    base: COLOR_LINKS,
    inverted: COLOR_LINKS,
  },
  base: {
    strong: COLOR_STRONG,
    base: COLOR_BASE,
    primary: COLOR_PRIMARY,
    primaryDark: COLOR_PRIMARY_DARK,
    secondary: COLOR_SECONDARY,
    muted: COLOR_MUTED,
    danger: COLOR_DANGER,
    inactive: COLOR_INACTIVE,
    inverted: {
      strong: COLOR_INVERTED_STRONG,
      base: COLOR_INVERTED_BASE,
      muted: COLOR_INVERTED_MUTED,
      inactive: COLOR_INVERTED_INACTIVE,
    },
  },
  button: {
    primary: {
      normal: {
        color: COLOR_INVERTED_BASE,
        backgroundColor: COLOR_PRIMARY,
        borderColor: COLOR_PRIMARY,
      },
      hover: {
        color: COLOR_INVERTED_BASE,
        backgroundColor: COLOR_BUTTON_PRIMARY_HOVER,
        borderColor: COLOR_BUTTON_PRIMARY_HOVER,
      },
      disabled: {
        color: COLOR_BUTTON_DISABLED_COLOR,
        backgroundColor: COLOR_BUTTON_DISABLED_BACKGROUND,
        borderColor: COLOR_BUTTON_DISABLED_BACKGROUND,
      },
    },
    secondary: {
      normal: {
        color: COLOR_INVERTED_BASE,
        backgroundColor: COLOR_BUTTON_SECONDARY,
        borderColor: COLOR_BUTTON_SECONDARY,
      },
      hover: {
        color: COLOR_INVERTED_BASE,
        backgroundColor: COLOR_BUTTON_SECONDARY_HOVER,
        borderColor: COLOR_BUTTON_SECONDARY_HOVER,
      },
      disabled: {
        color: COLOR_BUTTON_DISABLED_COLOR,
        backgroundColor: COLOR_BUTTON_DISABLED_BACKGROUND,
        borderColor: COLOR_BUTTON_DISABLED_BACKGROUND,
      },
    },
    link: {
      normal: {
        color: COLOR_LINKS,
        backgroundColor: COLOR_BUTTON_TRANSPARENT,
        borderColor: COLOR_BUTTON_TRANSPARENT,
      },
      hover: {
        color: COLOR_BUTTON_LINK_HOVER,
        backgroundColor: COLOR_BUTTON_LINK_HOVER_BACKGROUND,
        borderColor: COLOR_BUTTON_TRANSPARENT,
      },
      disabled: {
        color: COLOR_BUTTON_LINK_DISABLED,
        backgroundColor: COLOR_BUTTON_TRANSPARENT,
        borderColor: COLOR_BUTTON_TRANSPARENT,
      },
      inverted: {
        normal: {
          color: COLOR_BUTTON_LINK_INVERTED,
          backgroundColor: COLOR_BUTTON_TRANSPARENT,
          borderColor: COLOR_BUTTON_TRANSPARENT,
        },
        hover: {
          color: COLOR_INVERTED_BASE,
          backgroundColor: COLOR_BUTTON_LINK_INVERTED_HOVER_BACKGROUND,
          borderColor: COLOR_BUTTON_LINK_INVERTED_HOVER_BORDER,
        },
        disabled: {
          color: COLOR_BUTTON_LINK_INVERTED_DISABLED,
          backgroundColor: COLOR_BUTTON_TRANSPARENT,
          borderColor: COLOR_BUTTON_TRANSPARENT,
        },
      },
    },
    muted: {
      normal: {
        color: COLOR_BUTTON_MUTED_COLOR,
        backgroundColor: COLOR_BUTTON_TRANSPARENT,
        borderColor: COLOR_BUTTON_TRANSPARENT,
      },
      hover: {
        color: COLOR_BUTTON_MUTED_HOVER_COLOR,
        backgroundColor: COLOR_BUTTON_MUTED_HOVER_BACKGROUND,
        borderColor: COLOR_BUTTON_MUTED_HOVER_BORDER,
      },
      disabled: {
        color: COLOR_BUTTON_DISABLED_COLOR,
        backgroundColor: COLOR_BUTTON_DISABLED_BACKGROUND,
        borderColor: COLOR_BUTTON_DISABLED_BACKGROUND,
      },
    },
    warning: {
      normal: {
        color: COLOR_INVERTED_BASE,
        backgroundColor: COLOR_BUTTON_WARNING,
        borderColor: COLOR_BUTTON_WARNING,
      },
      hover: {
        color: COLOR_INVERTED_BASE,
        backgroundColor: COLOR_BUTTON_WARNING_HOVER,
        borderColor: COLOR_BUTTON_WARNING_HOVER,
      },
      disabled: {
        color: COLOR_BUTTON_DISABLED_COLOR,
        backgroundColor: COLOR_BUTTON_DISABLED_BACKGROUND,
        borderColor: COLOR_BUTTON_DISABLED_BACKGROUND,
      },
    },
    outline: {
      normal: {
        color: COLOR_BUTTON_MUTED_COLOR,
        backgroundColor: COLOR_BUTTON_TRANSPARENT,
        borderColor: COLOR_BUTTON_OUTLINE_BORDER,
      },
      hover: {
        color: COLOR_BUTTON_MUTED_COLOR,
        backgroundColor: COLOR_BUTTON_TRANSPARENT,
        borderColor: COLOR_BUTTON_MUTED_COLOR,
      },
      disabled: {
        color: COLOR_BUTTON_OUTLINE_DISABLED,
        backgroundColor: COLOR_BUTTON_TRANSPARENT,
        borderColor: COLOR_BUTTON_OUTLINE_DISABLED_BORDER,
      },
      inverted: {
        normal: {
          color: COLOR_BUTTON_OUTLINE_INVERTED,
          backgroundColor: COLOR_BUTTON_TRANSPARENT,
          borderColor: COLOR_BUTTON_OUTLINE_INVERTED_BORDER,
        },
        hover: {
          color: COLOR_INVERTED_BASE,
          backgroundColor: COLOR_BUTTON_OUTLINE_INVERTED_HOVER_BACKGROUND,
          borderColor: COLOR_BUTTON_OUTLINE_INVERTED_HOVER_BORDER,
        },
        disabled: {
          color: COLOR_BUTTON_OUTLINE_INVERTED_DISABLED,
          backgroundColor: COLOR_BUTTON_OUTLINE_INVERTED_DISABLED_BACKGROUND,
          borderColor: COLOR_BUTTON_OUTLINE_INVERTED_DISABLED_BORDER,
        },
      },
    },
  },
  background: {
    base: COLOR_BACKGROUND_COLOR_BASE,
    baseDark: COLOR_BACKGROUND_COLOR_BASE_DARK,
    medium: COLOR_BACKGROUND_COLOR_MEDIUM_GREY,
    muted: COLOR_BACKGROUND_COLOR_MUTED,
    inactive: COLOR_BACKGROUND_COLOR_INACTIVE,
    alpha: COLOR_BACKGROUND_COLOR_ALPHA,
    warning: COLOR_BACKGROUND_COLOR_WARNING,
    error: COLOR_BACKGROUND_COLOR_ERROR,
    standout: COLOR_BACKGROUND_COLOR_STANDOUT,
    primary: COLOR_BACKGROUND_COLOR_PRIMARY,
    primarySolid: COLOR_BACKGROUND_COLOR_PRIMARY_SOLID,
    page: COLOR_BACKGROUND_COLOR_BASE,
    surface: COLOR_SURFACE,
    sectionHeader: COLOR_BACKGROUND_COLOR_STANDOUT,
    am: {
      default: COLOR_BACKGROUND_COLOR_AM,
      dark: COLOR_BACKGROUND_COLOR_AM_DARK,
    },
    pm: {
      default: COLOR_BACKGROUND_COLOR_PM,
      dark: COLOR_BACKGROUND_COLOR_PM_DARK,
    },
    noc: {
      default: COLOR_BACKGROUND_COLOR_NOC,
      dark: COLOR_BACKGROUND_COLOR_NOC_DARK,
    },
    chat: {
      incoming: COLOR_CHAT_MESSAGE_INCOMING,
      outgoing: COLOR_CHAT_MESSAGE_OUTGOING,
    },
    inverted: {
      muted: COLOR_BACKGROUND_COLOR_INVERTED_MUTED,
    },
  },
  border: {
    base: COLOR_BORDER_COLOR_BASE,
    muted: COLOR_BORDER_COLOR_MUTED,
    strong: COLOR_BORDER_COLOR_STRONG,
    primary: COLOR_BORDER_COLOR_PRIMARY,
    warning: COLOR_BORDER_COLOR_WARNING,
    error: COLOR_BORDER_COLOR_ERROR,
  },
  navbar: {
    avatarBackground: COLOR_NAVBAR_AVATAR_BACKGROUND,
    avatarText: COLOR_NAVBAR_AVATAR_TEXT,
    gradient: COLOR_NAVBAR_GRADIENT,
    itemContent: COLOR_NAVBAR_ITEM_CONTENT,
    itemContentSelected: COLOR_NAVBAR_ITEM_CONTENT_SELECTED,
    itemSelectedBackground: COLOR_NAVBAR_ITEM_SELECTED_BACKGROUND,
    sectionTitle: COLOR_NAVBAR_SECTION_TITLE,
    sectionDivider: COLOR_NAVBAR_SECTION_DIVIDER,
    userName: COLOR_NAVBAR_USERNAME,
  },
  floatingNavigation: {
    background: COLOR_FLOATING_NAVIGATION_BACKGROUND,
    selectedBackground: COLOR_FLOATING_NAVIGATION_SELECTED_BACKGROUND,
  },
  shiftCard: {
    borderGradient: COLOR_SHIFT_CARD_BORDER_GRADIENT,
  },
  avatar: {
    initialsBackground: COLOR_AVATAR_INITIALS_BACKGROUND,
  },
  icon: {
    default: COLOR_STRONG,
    muted: COLOR_MUTED,
    link: COLOR_LINKS,
    inverted: {
      default: COLOR_INVERTED_STRONG,
      muted: COLOR_INVERTED_MUTED,
      link: COLOR_LINKS,
    },
  },
  progress: {
    positive: COLOR_PROGRESS_POSITIVE,
    pending: COLOR_PROGRESS_PENDING,
    negative: COLOR_PROGRESS_NEGATIVE,
    neutral: COLOR_PROGRESS_NEUTRAL,
  },
  statusRing: {
    positive: COLOR_STATUS_RING_POSITIVE,
    warning: COLOR_STATUS_RING_WARNING,
    negative: COLOR_STATUS_RING_NEGATIVE,
    neutral: COLOR_STATUS_RING_NEUTRAL,
  },
  statusChip: {
    success: {
      background: COLOR_BACKGROUND_COLOR_SUCCESS,
      text: COLOR_PROGRESS_POSITIVE,
    },
    error: {
      background: COLOR_BACKGROUND_COLOR_ERROR,
      text: COLOR_DANGER,
    },
    warning: {
      background: COLOR_BACKGROUND_COLOR_WARNING,
      text: COLOR_STATUS_WARNING,
    },
    info: {
      background: COLOR_BACKGROUND_COLOR_PRIMARY,
      text: COLOR_PRIMARY,
    },
    muted: {
      background: COLOR_BACKGROUND_COLOR_MUTED,
      text: COLOR_MUTED,
    },
    pending: {
      background: COLOR_BACKGROUND_COLOR_PENDING,
      text: COLOR_STATUS_PENDING,
    },
  },
  chip: {
    borderColor: COLOR_TEXT_PRIMARY,
    backgroundColor: "transparent",
  },
  subtleAction: {
    color: COLOR_MUTED,
    hoverColor: COLOR_TEXT_PRIMARY,
    hoverBackground: COLOR_SUBTLE_ACTION_HOVER_BACKGROUND,
  },
  listItem: {
    selectedBackground: COLOR_LIST_ITEM_SELECTED_BACKGROUND,
    hoverBackground: COLOR_SUBTLE_ACTION_HOVER_BACKGROUND,
  },
};
